import { useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  api,
  ApiError,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorText,
  Input,
  Label,
} from '@dataroom/shared';
import {
  KEYS_ERROR_MESSAGES,
  keysCall,
  KIT_CODE_HINT,
  KIT_CODE_PLACEHOLDER,
  readKitFile,
  UNLOCK_POLL_MS,
} from '@/lib/keys';

/**
 * UNLOCK (ADR-0069 §3): the page a kit holder opens when the service is
 * LOCKED — its server keys were replaced, so its data keys do not open until
 * the company recovery kit restores them. No sign-in (sign-in itself needs the
 * locked keys): the kit file and its recovery code are the credential.
 *
 * Public and outside the app's layout; no analytics tag ever loads here
 * (main.tsx; links here are full page loads, App.tsx reloads any other way in)
 * and the path carries a strict CSP (public/_headers). The file and the code
 * go only to the API, and only on submit.
 *
 * After a successful unlock the API restarts itself: the page asks every few
 * seconds until the locked server is gone (its status answers 404), then says
 * so and offers sign-in.
 */

interface LockStatus {
  keys: 'locked';
  unlock: 'kit' | 'not_possible';
  detail?: string;
}

const isNotLocked = (error: unknown) => error instanceof ApiError && error.status === 404;

export function UnlockPage() {
  const status = useQuery({
    queryKey: ['recovery', 'status'],
    queryFn: () => api.get<LockStatus>('/recovery/status'),
    retry: false,
  });
  const [file, setFile] = useState<File | null>(null);
  const [code, setCode] = useState('');
  const unlock = useMutation({
    mutationFn: async () => {
      const kit = await readKitFile(file);
      return keysCall(() =>
        api.post<{ unlocked: boolean; versions: number }>('/recovery/unlock', {
          kit,
          recoveryCode: code,
        }),
      );
    },
    onSuccess: () => setCode(''),
  });

  if (unlock.isSuccess) {
    return (
      <Shell>
        <AfterUnlock />
      </Shell>
    );
  }

  return (
    <Shell>
      {status.isPending ? (
        <p className="text-sm text-muted-foreground">Checking the service…</p>
      ) : isNotLocked(status.error) ? (
        <Card>
          <CardContent className="py-4 text-sm">
            ClosingVault is not locked. There is nothing to unlock —{' '}
            <a className="underline" href="/login">
              sign in as usual
            </a>
            .
          </CardContent>
        </Card>
      ) : status.error ? (
        <div className="space-y-2">
          <ErrorText error={status.error} />
          <Button variant="outline" onClick={() => void status.refetch()}>
            Check again
          </Button>
        </div>
      ) : status.data.unlock === 'not_possible' ? (
        <Card>
          <CardHeader>
            <CardTitle>The recovery kit can’t fix this problem</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>{status.data.detail ?? KEYS_ERROR_MESSAGES.not_unlockable}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>ClosingVault is locked</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>
              ClosingVault’s server keys were changed, so it can’t open anyone’s data until you
              unlock it with the company recovery kit. You need the kit file and its code from the
              company password manager.
            </p>
            <form
              className="grid gap-3"
              aria-label="Unlock with the recovery kit"
              onSubmit={(e) => {
                e.preventDefault();
                unlock.mutate();
              }}
            >
              <div className="grid gap-1">
                <Label htmlFor="unlock-kit-file">Kit file</Label>
                <input
                  id="unlock-kit-file"
                  type="file"
                  accept="application/json,.json"
                  // w-full: the native picker's own width would widen the page on a phone.
                  className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="unlock-kit-code">Recovery code</Label>
                <Input
                  id="unlock-kit-code"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={KIT_CODE_PLACEHOLDER}
                  aria-describedby="unlock-kit-code-hint"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <p id="unlock-kit-code-hint" className="text-xs text-muted-foreground">
                  {KIT_CODE_HINT}
                </p>
              </div>
              <p className="text-muted-foreground">You get 5 tries every 15 minutes.</p>
              <Button type="submit" disabled={unlock.isPending || code.trim() === ''}>
                {unlock.isPending ? 'Unlocking… (this can take a minute)' : 'Unlock'}
              </Button>
              <ErrorText error={unlock.error} />
            </form>
          </CardContent>
        </Card>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <main className="mx-auto w-full max-w-xl space-y-6 px-4 py-10">
      <h1 className="text-2xl font-semibold">Unlock ClosingVault</h1>
      {children}
    </main>
  );
}

/** Unlocked: wait for the restarted service, then send the holder to sign in and Verify. */
function AfterUnlock() {
  const up = useQuery({
    queryKey: ['recovery', 'status', 'after-unlock'],
    queryFn: () => api.get<LockStatus>('/recovery/status'),
    retry: false,
    // Until the locked server is gone (404); while it restarts, any answer means "not yet".
    refetchInterval: (query) => (isNotLocked(query.state.error) ? false : UNLOCK_POLL_MS),
  });
  const running = isNotLocked(up.error);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{running ? 'ClosingVault is unlocked and running.' : 'Starting up…'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm" data-testid="after-unlock">
        {running ? (
          <>
            <p>
              <a
                className="inline-flex rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground"
                href="/login"
              >
                Sign in
              </a>
            </p>
            <p>Then open Keys and Verify the kit with the same file and code.</p>
          </>
        ) : (
          <p>
            The recovery kit worked. ClosingVault is restarting; this page updates by itself in
            about a minute.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
